import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 4rem 0;
  background: #08111f;
  color: #f8fafc;
`;

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 1.6rem;
`;

const Header = styled.div`
  max-width: 760px;
  margin-bottom: 2.4rem;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #67e8f9;
`;

const Title = styled.h2`
  margin: 0 0 0.9rem;
  font-size: 2.4rem;
  line-height: 1.04;

  @media (max-width: 640px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.02rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.4rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  padding: 1.6rem;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.18);
`;

const CardTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.45rem;
`;

const Description = styled.p`
  margin: 0 0 1rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const MetricList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.75rem;
`;

const Metric = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 16px;
  background: rgba(30, 41, 59, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.12);

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const MetricName = styled.span`
  font-weight: 700;
  color: #f8fafc;
`;

const MetricDescription = styled.span`
  color: #cbd5e1;
  line-height: 1.5;
`;

const methodologyCards = [
  {
    title: 'Final settlement',
    description:
      'Final payout stays anchored to the measurable outcome, not just the intermediate artifacts providers produced along the way.',
    metrics: [
      ['Outcome score', 'The final measurable score that determines earned reward at resolution.'],
      ['Edge vs baseline', 'Optional comparison against an external market or reference prior.'],
      ['True-up', 'Final payout nets any prior early releases against the earned amount.'],
      ['Dispute window', 'Operators and principals can inspect evidence before settlement closes.'],
    ],
  },
  {
    title: 'Early settlement for long-horizon requests',
    description:
      'NLM-backed previews estimate expected final reward from checkpoint evidence, then release only a capped fraction while keeping a reserve floor intact.',
    metrics: [
      ['Reserve floor', 'The minimum amount held back until final settlement.'],
      ['Checkpoint grades', 'Intermediate artifacts influence early release eligibility.'],
      ['Prediction interval', 'Preview confidence caps how much the platform can release.'],
      ['Release cap', 'Max early release stays below the configured basis-point threshold.'],
    ],
  },
];

const MethodologySection = () => {
  return (
    <Section id="settlement">
      <Container>
        <Header>
          <Eyebrow>Settlement</Eyebrow>
          <Title>Settlement and scoring stay tied to the final truth source</Title>
          <Subtitle>
            Early settlement exists to reduce long waits, not to replace final resolution. The
            platform uses checkpoint grades as supporting evidence, then reconciles everything
            against the eventual outcome.
          </Subtitle>
        </Header>

        <Grid>
          {methodologyCards.map(card => (
            <Card key={card.title}>
              <CardTitle>{card.title}</CardTitle>
              <Description>{card.description}</Description>
              <MetricList>
                {card.metrics.map(([name, description]) => (
                  <Metric key={name}>
                    <MetricName>{name}</MetricName>
                    <MetricDescription>{description}</MetricDescription>
                  </Metric>
                ))}
              </MetricList>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default MethodologySection;
